import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { startPopupTip, stopPopupTip } from '../audio';

const TIP_TITLE = 'THE JERSEY IS WAITING';
const TIP_LINE_DESKTOP = 'Choose the name and number that enter the arena.';
const CHAR_MS = 48;
const SPACE_MS = 90;
const LINE_PAUSE_MS = 420;

type Props = {
  className?: string;
  visible: boolean;
  /** Override typewriter title (locker default: jersey waiting). */
  title?: string;
  /** Override typewriter body line (mobile uses a shorter copy). */
  line?: string;
  /** OBJECTIVE tab label */
  tab?: string;
};

/**
 * Sci-fi objective tip — typewriter title + instruction line.
 */
export function LockerObjectiveTip({
  className = '',
  visible,
  title = TIP_TITLE,
  line = TIP_LINE_DESKTOP,
  tab = 'OBJECTIVE',
}: Props) {
  const root = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [typedTitle, setTypedTitle] = useState('');
  const [typedLine, setTypedLine] = useState('');
  const [phase, setPhase] = useState<'title' | 'line' | 'done'>('title');

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setTypedTitle('');
      setTypedLine('');
      setPhase('title');
      startPopupTip({ gain: 0.48 });

      let i = 0;
      let timer = 0;
      const tipTitle = title;
      const tipLine = line;

      const schedule = (fn: () => void, ms: number) => {
        timer = window.setTimeout(fn, ms);
      };

      const tickLine = () => {
        i += 1;
        setTypedLine(tipLine.slice(0, i));
        if (i >= tipLine.length) {
          setPhase('done');
          stopPopupTip(220);
          return;
        }
        const next = tipLine[i - 1] === ' ' ? SPACE_MS : CHAR_MS;
        schedule(tickLine, next);
      };

      const tickTitle = () => {
        i += 1;
        setTypedTitle(tipTitle.slice(0, i));
        if (i >= tipTitle.length) {
          i = 0;
          setPhase('line');
          schedule(tickLine, LINE_PAUSE_MS);
          return;
        }
        const next = tipTitle[i - 1] === ' ' ? SPACE_MS : CHAR_MS;
        schedule(tickTitle, next);
      };

      schedule(tickTitle, CHAR_MS);

      return () => {
        window.clearTimeout(timer);
        stopPopupTip(120);
      };
    }

    stopPopupTip();
    setPhase('done');
    if (!root.current) {
      setMounted(false);
      setTypedTitle('');
      setTypedLine('');
      return;
    }

    const el = root.current;
    gsap.to(el, {
      opacity: 0,
      y: -10,
      scale: 0.97,
      duration: 0.28,
      ease: 'power2.in',
      onComplete: () => {
        setMounted(false);
        setTypedTitle('');
        setTypedLine('');
      },
    });
  }, [visible, title, line]);

  useEffect(() => {
    if (!visible || !mounted || !root.current) return;
    gsap.fromTo(
      root.current,
      { opacity: 0, y: 16, scale: 0.96 },
      { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: 'power3.out' },
    );
  }, [visible, mounted]);

  if (!mounted) return null;

  return (
    <div
      className={`hud-tip ${className}`.trim()}
      ref={root}
      role="status"
      aria-live="polite"
    >
      <span className="hud-tip__glow" aria-hidden />
      <div className="hud-tip__body">
        <div className="hud-tip__tab">
          <span>{tab}</span>
        </div>

        <p className="hud-tip__title">
          {typedTitle}
          {phase === 'title' && (
            <span className="hud-tip__caret" aria-hidden>
              |
            </span>
          )}
        </p>

        <p className="hud-tip__line">
          {typedLine}
          {(phase === 'line' || phase === 'done') && (
            <span
              className={`hud-tip__caret${phase === 'done' ? ' is-done' : ''}`}
              aria-hidden
            >
              |
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
